/**
 * useOffers Hook
 * React hook for managing offers with real API integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { offersAPI, ProductOffer, APIError } from '../services/api';

interface UseOffersOptions {
  status?: string;
  autoFetch?: boolean;
}

interface UseOffersReturn {
  offers: ProductOffer[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  acceptOffer: (offerId: string) => Promise<boolean>;
  declineOffer: (offerId: string) => Promise<boolean>;
  confirmPayment: (offerId: string, notes?: string) => Promise<boolean>;
  counterOffer: (offerId: string, amount: number) => Promise<boolean>;
  isSubmitting: boolean;
}

export function useOffers(options: UseOffersOptions = {}): UseOffersReturn {
  const { status, autoFetch = true } = options;

  const [offers, setOffers] = useState<ProductOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Prevent double-click issues
  const submitRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await offersAPI.getAll(status);
      setOffers(data.offers || []);
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to load offers';
      setError(message);
      console.error('useOffers fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (!autoFetch) return;
    refresh();
    // Poll every 30s so new pending offers surface for admin without a reload.
    const id = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(id);
  }, [refresh, autoFetch]);

  const acceptOffer = useCallback(async (offerId: string): Promise<boolean> => {
    if (submitRef.current) return false;
    submitRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await offersAPI.accept(offerId);
      if (result.success) {
        // Update local state optimistically
        setOffers(prev => prev.map(o =>
          o.id === offerId ? { ...o, status: 'accepted' as const } : o
        ));
        // Refresh to get latest server state
        await refresh();
        return true;
      }
      return false;
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to accept offer';
      setError(message);
      return false;
    } finally {
      setIsSubmitting(false);
      submitRef.current = false;
    }
  }, [refresh]);

  const declineOffer = useCallback(async (offerId: string): Promise<boolean> => {
    if (submitRef.current) return false;
    submitRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await offersAPI.decline(offerId);
      if (result.success) {
        setOffers(prev => prev.map(o =>
          o.id === offerId ? { ...o, status: 'rejected' as const } : o
        ));
        await refresh();
        return true;
      }
      return false;
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to decline offer';
      setError(message);
      return false;
    } finally {
      setIsSubmitting(false);
      submitRef.current = false;
    }
  }, [refresh]);

  const confirmPayment = useCallback(async (offerId: string, notes?: string): Promise<boolean> => {
    if (submitRef.current) return false;
    submitRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await offersAPI.confirmPayment(offerId, notes);
      if (result.success) {
        // Remove from pending offers list or update status
        setOffers(prev => prev.filter(o => o.id !== offerId));
        await refresh();
        return true;
      }
      return false;
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to confirm payment';
      setError(message);
      return false;
    } finally {
      setIsSubmitting(false);
      submitRef.current = false;
    }
  }, [refresh]);

  const counterOffer = useCallback(async (offerId: string, amount: number): Promise<boolean> => {
    if (submitRef.current) return false;
    submitRef.current = true;
    setIsSubmitting(true);

    try {
      const result = await offersAPI.counter(offerId, amount);
      if (result.success) {
        setOffers(prev => prev.map(o =>
          o.id === offerId ? { ...o, status: 'countered' as const, counter_amount: amount } : o
        ));
        await refresh();
        return true;
      }
      return false;
    } catch (err) {
      const message = err instanceof APIError ? err.message : 'Failed to counter offer';
      setError(message);
      return false;
    } finally {
      setIsSubmitting(false);
      submitRef.current = false;
    }
  }, [refresh]);

  return {
    offers,
    loading,
    error,
    refresh,
    acceptOffer,
    declineOffer,
    confirmPayment,
    counterOffer,
    isSubmitting,
  };
}

// Hook for pending offers specifically (admin view)
export function usePendingOffers() {
  return useOffers({ status: 'pending' });
}

// Hook for accepted offers awaiting payment
export function useAwaitingPaymentOffers() {
  return useOffers({ status: 'accepted' });
}

export default useOffers;
